import { UNCATEGORIZED_CATEGORY_ID } from './categoryStorage'
import type { FoodItem } from '../types'
import { isPosExtraItem } from '@shared/lib/posExtraItem'

export function effectiveMenuCategoryId(
  item: FoodItem,
  validCategoryIds: Set<string>,
): string {
  const c = item.categoryId
  if (c && validCategoryIds.has(c)) return c
  return UNCATEGORIZED_CATEGORY_ID
}

/** Move menu item to another category (or reorder within category): updates categoryId and sortIndex. */
export function moveOrReorderMenuItem(
  allItems: FoodItem[],
  validCategoryIds: Set<string>,
  draggedId: string,
  targetCategoryId: string,
  beforeId: string | null,
): FoodItem[] {
  const eff = (i: FoodItem) => effectiveMenuCategoryId(i, validCategoryIds)
  const dragged = allItems.find((i) => i.id === draggedId)
  if (!dragged) return allItems
  if (isPosExtraItem(dragged)) return allItems
  const sourceCategory = eff(dragged)

  const patchedDragged: FoodItem =
    targetCategoryId === UNCATEGORIZED_CATEGORY_ID
      ? { ...dragged, categoryId: undefined }
      : { ...dragged, categoryId: targetCategoryId }

  let working = allItems.map((i) => (i.id === draggedId ? patchedDragged : i))

  const eff2 = (i: FoodItem) => effectiveMenuCategoryId(i, validCategoryIds)
  const others = working
    .filter((i) => eff2(i) === targetCategoryId && i.id !== draggedId)
    .sort(
      (a, b) =>
        (a.sortIndex ?? 0) - (b.sortIndex ?? 0) || a.name.localeCompare(b.name),
    )
  const ids = [...others.map((i) => i.id)]
  let insertAt = ids.length
  if (beforeId && beforeId !== draggedId) {
    const ti = ids.indexOf(beforeId)
    if (ti >= 0) insertAt = ti
  }
  ids.splice(insertAt, 0, draggedId)

  const reindexedTarget = new Map(ids.map((id, i) => [id, i]))
  working = working.map((i) => {
    if (eff2(i) !== targetCategoryId) return i
    const si = reindexedTarget.get(i.id)
    if (si === undefined) return i
    return { ...i, sortIndex: si }
  })

  if (sourceCategory !== targetCategoryId) {
    const remaining = working
      .filter((i) => eff2(i) === sourceCategory)
      .sort(
        (a, b) =>
          (a.sortIndex ?? 0) - (b.sortIndex ?? 0) || a.name.localeCompare(b.name),
      )
    const sids = remaining.map((i) => i.id)
    const smap = new Map(sids.map((id, i) => [id, i]))
    working = working.map((i) => {
      if (eff2(i) !== sourceCategory) return i
      const si = smap.get(i.id)
      if (si === undefined) return i
      return { ...i, sortIndex: si }
    })
  }

  return working
}

/** Move multiple menu items as a block preserving relative order. */
export function moveOrReorderMenuItems(
  allItems: FoodItem[],
  validCategoryIds: Set<string>,
  draggedIds: string[],
  targetCategoryId: string,
  beforeId: string | null,
): FoodItem[] {
  if (draggedIds.length === 0) return allItems
  if (draggedIds.length === 1) {
    return moveOrReorderMenuItem(allItems, validCategoryIds, draggedIds[0]!, targetCategoryId, beforeId)
  }

  const eff = (i: FoodItem) => effectiveMenuCategoryId(i, validCategoryIds)
  const idSet = new Set(draggedIds)
  const draggedItems = allItems.filter((i) => idSet.has(i.id) && !isPosExtraItem(i))
  if (draggedItems.length === 0) return allItems

  const sourceCategories = new Set(draggedItems.map(eff))
  const sortedDragged = [...draggedItems].sort(
    (a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0) || a.name.localeCompare(b.name),
  )
  const orderedIds = sortedDragged.map((i) => i.id)

  let working = allItems.map((i) => {
    if (!idSet.has(i.id)) return i
    return targetCategoryId === UNCATEGORIZED_CATEGORY_ID
      ? { ...i, categoryId: undefined }
      : { ...i, categoryId: targetCategoryId }
  })

  const eff2 = (i: FoodItem) => effectiveMenuCategoryId(i, validCategoryIds)
  const others = working
    .filter((i) => eff2(i) === targetCategoryId && !idSet.has(i.id))
    .sort(
      (a, b) =>
        (a.sortIndex ?? 0) - (b.sortIndex ?? 0) || a.name.localeCompare(b.name),
    )
  const ids = others.map((i) => i.id)
  let insertAt = ids.length
  if (beforeId && !idSet.has(beforeId)) {
    const ti = ids.indexOf(beforeId)
    if (ti >= 0) insertAt = ti
  }
  ids.splice(insertAt, 0, ...orderedIds)

  const reindexedTarget = new Map(ids.map((id, i) => [id, i]))
  working = working.map((i) => {
    if (eff2(i) !== targetCategoryId) return i
    const si = reindexedTarget.get(i.id)
    if (si === undefined) return i
    return { ...i, sortIndex: si }
  })

  for (const sourceCategory of sourceCategories) {
    if (sourceCategory === targetCategoryId) continue
    const remaining = working
      .filter((i) => eff2(i) === sourceCategory)
      .sort(
        (a, b) =>
          (a.sortIndex ?? 0) - (b.sortIndex ?? 0) || a.name.localeCompare(b.name),
      )
    const sids = remaining.map((i) => i.id)
    const smap = new Map(sids.map((id, i) => [id, i]))
    working = working.map((i) => {
      if (eff2(i) !== sourceCategory) return i
      const si = smap.get(i.id)
      if (si === undefined) return i
      return { ...i, sortIndex: si }
    })
  }

  return working
}
